import { Navigate, NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { BookOpen, Columns2, FileText, Folder, Package, LogOut, ExternalLink, Loader2, Settings, BarChart3, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { KINDS } from "./adminConfig";
import Logo from "../../components/Logo";

const ICONS = { reviews: FileText, guides: BookOpen, comparisons: Columns2, products: Package, categories: Folder };
const ANALYST_PATHS = ["/admin/analytics", "/admin/settings"];

function navItems(role) {
  const analytics = { key: "analytics", label: "Analytics", Icon: BarChart3 };
  if (role !== "admin") return [analytics];
  return [
    ...Object.entries(KINDS).map(([key, cfg]) => ({ key, label: cfg.label, Icon: ICONS[key] })),
    analytics,
    { key: "team", label: "Team", Icon: Users },
  ];
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper text-neutral-500" data-testid="admin-loading">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin" && !ANALYST_PATHS.includes(pathname)) return <Navigate to="/admin/analytics" replace />;

  return (
    <div className="min-h-screen bg-paper lg:grid lg:grid-cols-[248px_1fr]" data-testid="admin-layout">
      <aside className="bg-surface border-b lg:border-b-0 lg:border-r border-neutral-200 lg:sticky lg:top-0 lg:h-screen flex lg:flex-col">
        <div className="px-6 py-5 lg:py-7 hidden lg:block">
          <Logo testid="admin-logo" />
          <p className="eyebrow mt-2 text-neutral-500">{user.role === "admin" ? "Editor" : "Analytics"}</p>
        </div>
        <nav className="flex lg:flex-col gap-1 px-3 py-3 lg:py-0 overflow-x-auto flex-1 [mask-image:linear-gradient(to_right,black_85%,transparent)] lg:[mask-image:none]" aria-label="Admin">
          {navItems(user.role).map(({ key, label, Icon }) => {
            return (
              <NavLink
                key={key}
                to={`/admin/${key}`}
                data-testid={`admin-nav-${key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive ? "bg-indigo-50 text-indigo-900" : "text-neutral-700 hover:bg-neutral-100"
                  }`
                }
              >
                <Icon size={16} /> {label}
              </NavLink>
            );
          })}
        </nav>
        <div className="hidden lg:block border-t border-neutral-200 p-4 space-y-1">
          <p className="px-3 text-xs text-neutral-500 truncate mb-2" data-testid="admin-user-email">{user.email}</p>
          <NavLink
            to="/admin/settings"
            data-testid="admin-nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive ? "bg-indigo-50 text-indigo-900 font-medium" : "text-neutral-700 hover:bg-neutral-100"
              }`
            }
          >
            <Settings size={15} /> Settings
          </NavLink>
          <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-neutral-700 hover:bg-neutral-100 transition-colors" data-testid="admin-view-site">
            <ExternalLink size={15} /> View site
          </Link>
          <button type="button" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-neutral-700 hover:bg-red-50 hover:text-red-700 transition-colors" data-testid="admin-logout-button">
            <LogOut size={15} /> Sign out
          </button>
        </div>
        <NavLink to="/admin/settings" aria-label="Settings" className="lg:hidden px-3 flex items-center text-neutral-600 hover:text-indigo-800" data-testid="admin-nav-settings-mobile">
          <Settings size={17} />
        </NavLink>
        <button type="button" onClick={logout} aria-label="Sign out" className="lg:hidden px-4 text-neutral-600 hover:text-red-700" data-testid="admin-logout-button-mobile">
          <LogOut size={17} />
        </button>
      </aside>
      <main className="min-w-0 px-5 sm:px-8 lg:px-12 py-10 lg:py-12">
        <Outlet />
      </main>
    </div>
  );
}
